import jsPDF from "jspdf";
import { htmlToBlocks, loadLogoDataUrl, funderSlug, type InlineRun } from "./htmlToBlocks";

interface ExportArgs {
  html: string;
  grantName: string;
  funder: string;
  ptaName: string;
  ptaLogoUrl?: string | null;
}

export async function exportGrantLetterPdf({ html, grantName, funder, ptaName, ptaLogoUrl }: ExportArgs) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  // Logo top-left
  const logo = await loadLogoDataUrl(ptaLogoUrl ?? null);
  if (logo) {
    const h = 18;
    const w = (logo.width / logo.height) * h;
    doc.addImage(logo.dataUrl, "PNG", margin, y, w, h);
  }

  // PTA name top-right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text(ptaName, pageWidth - margin, y + 6, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(new Date().toLocaleDateString("en-GB"), pageWidth - margin, y + 12, { align: "right" });

  y += 26;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Subject line
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const subject = `Re: ${grantName} — ${funder}`;
  const subjectLines = doc.splitTextToSize(subject, maxWidth);
  subjectLines.forEach((line: string) => {
    if (y > pageHeight - margin) { doc.addPage(); y = margin; }
    doc.text(line, margin, y);
    y += 6;
  });
  y += 4;

  // Body
  const blocks = htmlToBlocks(html);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  const writeRuns = (runs: InlineRun[], indent = 0) => {
    const lineHeight = 5.5;
    // Build a single string with style segments. jsPDF doesn't support mixed inline styles
    // per chunk on splitTextToSize, so we degrade by joining text but applying bold/italic
    // for whole-paragraph runs if uniform.
    const allBold = runs.length > 0 && runs.every((r) => r.bold);
    const allItalic = runs.length > 0 && runs.every((r) => r.italic);
    const style = allBold && allItalic ? "bolditalic" : allBold ? "bold" : allItalic ? "italic" : "normal";
    doc.setFont("helvetica", style);
    const text = runs.map((r) => r.text).join("").replace(/\s+/g, " ").trim();
    if (!text) return;
    const lines = doc.splitTextToSize(text, maxWidth - indent);
    lines.forEach((line: string) => {
      if (y > pageHeight - margin) { doc.addPage(); y = margin; }
      doc.text(line, margin + indent, y);
      y += lineHeight;
    });
  };

  blocks.forEach((b) => {
    if (b.type === "heading") {
      y += 2;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(b.level === 1 ? 14 : b.level === 2 ? 12 : 11);
      writeRuns(b.runs);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      y += 1;
    } else if (b.type === "paragraph") {
      writeRuns(b.runs);
      y += 2;
    } else if (b.type === "list") {
      b.items.forEach((item, i) => {
        const bullet = b.ordered ? `${i + 1}. ` : "•  ";
        const text = item.map((r) => r.text).join("").replace(/\s+/g, " ").trim();
        if (!text) return;
        const lines = doc.splitTextToSize(`${bullet}${text}`, maxWidth - 4);
        lines.forEach((line: string) => {
          if (y > pageHeight - margin) { doc.addPage(); y = margin; }
          doc.text(line, margin + 4, y);
          y += 5.5;
        });
      });
      y += 2;
    }
  });

  // Footer on each page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`${ptaName}`, margin, pageHeight - 8);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  doc.save(`Grant-Application-${funderSlug(funder)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
